import { modules } from '~/shared/config'
import { model as i18n } from '!/i18n'
import { register } from '!/repository'
import { model as router } from '!/router'
import { checkIsSearchQueryValid, nay } from '@/helpers'
import { api } from '@setplex/tria-api'
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { or } from 'patronum'
import { type ChangeEvent, type FormEvent } from 'react'
import { MINIMUM_QUERY_LENGTH_OF_SEARCH_INPUT } from '~/shared/constants/strings'
import { ContentType, searchInit } from '../../root/model'
import { formatSearchInputPlaceholder } from './lib'

const SEARCH_REQUEST_DELAY = 1200
const TOOLTIP_VISIBLE_DELAY = 2000

// Events

const handleChange = createEvent<{ '<event>': ChangeEvent<HTMLInputElement> }>()
const handleSubmit = createEvent<{ '<event>': FormEvent<HTMLFormElement> }>()
const deleteValue = createEvent()

// Effects

let timeout: ReturnType<typeof setTimeout>
const timeoutFx = createEffect<void, void>(
  () =>
    new Promise((resolve) => {
      clearInterval(timeout)
      timeout = setTimeout(resolve, SEARCH_REQUEST_DELAY)
    })
)
const cancelTimeoutFx = createEffect<void, void>(() => clearTimeout(timeout))

let timeoutTooltip: ReturnType<typeof setTimeout>
const timeoutTooltipFx = createEffect<void, void>(
  () =>
    new Promise((resolve) => {
      clearInterval(timeoutTooltip)
      timeoutTooltip = setTimeout(resolve, TOOLTIP_VISIBLE_DELAY)
    })
)
const cancelTimeoutTooltipFx = createEffect<void, void>(() =>
  clearTimeout(timeoutTooltip)
)

// Stores

const $placeholder = combine(
  {
    i18n: i18n.$t,
    tvEnabled: modules.$tvEnabled,
    vodEnabled: modules.$vodEnabled,
    tvShowEnabled: modules.$tvShowEnabled,
    eventsEnabled: modules.$liveEventEnabled,
  },
  ({ i18n: { t }, tvEnabled, vodEnabled, tvShowEnabled, eventsEnabled }) => {
    return formatSearchInputPlaceholder({
      t,
      tvEnabled,
      vodEnabled,
      tvShowEnabled,
      eventsEnabled,
    })
  }
)

const $isRequestsPending = or(
  api.globalSearch.getSearchFx.pending,
  api.globalSearch.loadSearchEventsFx.pending,
  api.globalSearch.loadSearchTvChannelsFx.pending,
  api.globalSearch.loadSearchTvShowsFx.pending,
  api.globalSearch.loadSearchVodsFx.pending
)

export const $searchValue = createStore<string>('')
  .on(handleChange, (_, data) => data['<event>'].target.value)
  .reset([deleteValue, searchInit])

export const $isVisibleTooltip = createStore<boolean>(false).reset(searchInit)
export const $tooltipTitle = combine(i18n.$t, ({ t }) =>
  t('search_min_lengths_hint', {
    count: MINIMUM_QUERY_LENGTH_OF_SEARCH_INPUT,
  })
)

// Connections

sample({
  clock: handleChange,
  target: [timeoutFx, timeoutTooltipFx],
})

sample({
  clock: handleSubmit,
  target: [cancelTimeoutFx, cancelTimeoutTooltipFx],
})

// Disable the default behavior for the onSubmit, so that the search field value is not automatically set in the url and, accordingly, the page is not refreshing
sample({
  clock: [handleSubmit, timeoutFx.done],
  source: {
    searchValue: $searchValue,
    pending: $isRequestsPending,
  },
  filter: ({ searchValue, pending }) =>
    checkIsSearchQueryValid(searchValue) && nay(pending),
  fn: ({ searchValue }) => {
    const query = searchValue.replace(/^\s+|\s+$/gm, '')
    const pathname = '/modules/search'

    return {
      pathname: `${pathname}/${ContentType.All}`,
      search: `q=${encodeURIComponent(query)}`,
    }
  },
  target: router.navigatePush,
})

// navigate to initial search page if user enter not valid search query directly in url
sample({
  clock: searchInit,
  filter: ({ query }) =>
    query.has('q') && !checkIsSearchQueryValid(query.get('q') || ''),
  fn: () => ({ pathname: '/modules/search' }),
  target: router.navigatePush,
})

// synchronize search string from url with store
sample({
  clock: searchInit,
  filter: ({ query }) =>
    query.has('q') && checkIsSearchQueryValid(query.get('q') || ''),
  fn: ({ query }) => query.get('q') || '',
  target: $searchValue,
})

sample({
  clock: [handleSubmit, timeoutTooltipFx.done],
  source: $searchValue,
  fn: (value) => !checkIsSearchQueryValid(value),
  target: $isVisibleTooltip,
})

sample({
  clock: $searchValue,
  filter: (value: string) => checkIsSearchQueryValid(value),
  fn: (value) => !checkIsSearchQueryValid(value),
  target: $isVisibleTooltip,
})

register($placeholder, '$search/placeholder')
register($searchValue, '$search/searchValue')
register($isVisibleTooltip, '$search/isVisibleTooltip')
register($tooltipTitle, '$search/tooltipTitle')
register(handleChange, 'search/handleChange')
register(handleSubmit, 'search/handleSubmit')
register(deleteValue, 'search/deleteValue')
